import { Alert }                    from '@/components/ui/alert';
import { Button }                   from "@/components/ui/button"
import { Center}                    from "@chakra-ui/react";
import { Checkbox }                 from "@chakra-ui/react";
import { CheckboxGroup }            from "@chakra-ui/react";
import { Fieldset }                 from "@chakra-ui/react";
import { For }                      from "@chakra-ui/react";
import { Field }                    from '@/components/ui/field';
import { Grid }                     from '@chakra-ui/react';
import { GridItem }                 from '@chakra-ui/react';
import { Input }                    from '@chakra-ui/react';
import LanguageModel                from '@/models/services/fondations/LanguageModel';
import { ListCollection }           from '@chakra-ui/react';
import { PasswordInput }            from '@/components/ui/password-input';
import ReCAPTCHA                    from "react-google-recaptcha";
import React                        from 'react';
import { SelectContent }            from '@/components/ui/select';
import { SelectItem }               from '@/components/ui/select';
import { SelectRoot }               from '@/components/ui/select';
import { SelectTrigger }            from '@/components/ui/select';
import { SelectValueText }          from '@/components/ui/select';
import { Spinner }                  from "@chakra-ui/react";
import { Text }                     from '@chakra-ui/react';
import UserRegisterModel            from "@/models/services/fondations/UserRegisterModel";
import { createListCollection }     from '@chakra-ui/react';
import { toaster }                  from '@/components/ui/toaster';
import useApiCollection             from '@widesk-core/hooks/useApiCollection';
import useFetch                     from '@widesk-core/hooks/useFetch';
import { useController }            from 'react-hook-form';
import { useForm }                  from 'react-hook-form';
import { useSearchParams }          from 'react-router-dom';
import { useTranslation }           from 'react-i18next';
import useTranslations              from '@/hooks/useTranslations';
import getTextAndTranslateFromError from '@widesk-core/tools/getTextAndTranslateFromError';
import NewsletterModel              from '@/models/services/notification/NewsletterModel';
import { HiOutlineCheck }           from 'react-icons/hi2';

interface FormValues {
	confirmPassword: string;
	email: string;
	firstName: string;
	gender: string;
	language: string;
	lastName: string;
	newsletters: [];
	password: string;
}

const RegistrationForm = () => {

	const [searchParams] = useSearchParams();
	const prefilledEmail = searchParams.get('email') ?? '';

	const {
		control,
        formState: { errors, isSubmitting },
		getValues,
        handleSubmit,
        register,
		setError,
		setValue,
    } = useForm<FormValues>({
		defaultValues: { email: prefilledEmail },
	})

	const collectionLanguage = useApiCollection(LanguageModel);
	const { data: languageCollection } = useFetch(async () => {
		return await collectionLanguage.list();
	}, true);

	const collectionNewsletter = useApiCollection(NewsletterModel);
	const { data: newsletterCollection } = useFetch(async () => {
		return await collectionNewsletter.list();
	}, true);

	const CAPTCHA_SITE_KEY = import.meta.env.VITE_CAPTCHA_SITE_KEY;	

	const { t } = useTranslation();
	const { getNavigationTranslation } = useTranslations();

	const newsletters = useController({
		control,
		name: "newsletters",
		defaultValue: [],
	});
	
	const [genders, setGenders] = React.useState<ListCollection>();
	const [languages, setLanguages] = React.useState<ListCollection>();
	const [checkEmail, setCheckEmail] = React.useState(false);
	const [captchaValue, setCaptchaValue] = React.useState('');
	const [captchaKey, setCaptchaKey] = React.useState(1); // Permet de re-monter le composant captcha quand il y a une erreur

	React.useEffect(() => {
		if (languageCollection?.isLoaded) {
			const newLanguages = createListCollection({
				items: languageCollection.map(language => {
					return { label: t(`app.languages.${language.locale}`), value: language.urn || '' };
				}),
			});

			setLanguages(newLanguages);

			const newGenders = createListCollection({
				items: [
				  { label: t('app.selects.genders.masc') + ' ' + t('app.selects.genders.man'), value: '1' },
				  { label: t('app.selects.genders.fem') + ' ' + t('app.selects.genders.woman'), value: '2' },
				],
			});

			setGenders(newGenders);
		}
	}, [languageCollection?.isLoaded])

	if (!genders || !languages) {
		return (
			<Center height="100%" >
				<Spinner borderWidth="4px" size="xl" />
			</Center>
		)
	}

	const onSubmit = handleSubmit(async ({
		email,
		password,
		lastName,
		firstName,
		gender,
		language,
		newsletters,
	}) => {
		try {
			const userRegister = new UserRegisterModel();
			await userRegister.patch({
				captcha: 'true',
				email,
				confirmEmailUrl: location.origin + getNavigationTranslation('confirmationRegistration', 'path'),
				plainPassword: password,
				gender: parseInt(gender),
				firstName,
				lastName,
				userLanguages: [{ language }],
				newsletters: newsletters,
			}, {
				params: { 'g-recaptcha-response': captchaValue },
			});

			setCheckEmail(true);
		} catch (error: any) {
			setCaptchaKey(captchaKey + 1);
			setCaptchaValue('');

			type ErrorResponse = { constraintViolationList: { propertyPath: string, message: string }[] }

			const request = error?.connectorError?.request;
			const response = JSON.parse(request.response) as ErrorResponse;
			const violations = response.constraintViolationList ?? [];

			const isEmailError = violations.some(v => v.propertyPath === 'email');
			const isCaptchaError = violations.some(v => v.propertyPath === 'captcha');
			const isOtherError = !isEmailError && !isCaptchaError;

			if (isEmailError) {
				setError('email', { type: 'server', message: t('pages.registration.form.messages.errors.emailAlreadyUsed') });
				toaster.create({
					title: t('pages.registration.form.messages.errors.emailAlreadyUsed'),
					type: 'error',
					duration: 5000,
				});
			}

			if (isCaptchaError) {
				toaster.create({
					title: t('pages.registration.form.messages.errors.captcha'),
					type: 'error',
					duration: 5000,
				});
			}

			if (isOtherError) {
				toaster.create({
					title: `${getTextAndTranslateFromError(error)}`,
					type: 'error',
					duration: 5000,
				});
			}
		}
	})

	if (checkEmail) {
		return (
			<Alert status="warning" title={t('pages.registration.form.messages.success.title')} >
				{t('pages.registration.form.messages.success.description')}
			</Alert>
		)
	}

	return (
		<form onSubmit={onSubmit}>
			<Grid templateColumns="repeat(2, 1fr)" gap={5} >
				<GridItem colSpan={2} >
					<Field
						label={<Text className="poppins-semibold" fontWeight="bold">{t('pages.registration.form.inputs.email.label')}</Text>}
						required
					>
						<Input placeholder={t('pages.registration.form.inputs.email.placeholder')}
							{
								...register(
									"email", 
									{
										required: true,
										pattern: {
											value: /\S+@\S+\.\S+/,
											message: t('pages.security.emailBox.modals.forms.messages.errors.pattern'),
										}
									}
								)
							}
						/>
						{errors.email?.type === 'required' && <Text color="red">{t('pages.registration.form.inputs.email.errors.required')}</Text>}
						{errors.email?.type === 'pattern' && <Text color="red">{t('pages.security.emailBox.modals.forms.messages.errors.pattern')}</Text>}
						{errors.email?.type === 'server' && <Text color="red">{errors.email.message}</Text>}
					</Field>
				</GridItem>

				<GridItem colSpan={{ base: 2, lg: 1 }} >
					<Field
						label={<Text className="poppins-semibold" fontWeight="bold">{t('pages.registration.form.inputs.firstName.label')}</Text>}
						required
					>
						<Input
							placeholder={t('pages.registration.form.inputs.firstName.placeholder')}
							{
								...register(
									"firstName", 
									{ required: true }
								)
							}
						/>
						{
							errors.firstName && 
							errors.firstName.type === 'required' && 
							<Text 
								color="red"
							>
								{t('pages.registration.form.inputs.firstName.errors.required')}
							</Text>
						}
					</Field>
				</GridItem>

				<GridItem colSpan={{ base: 2, lg: 1 }} >
					<Field
						label={<Text className="poppins-semibold" fontWeight="bold">{t('pages.registration.form.inputs.lastName.label')}</Text>}
						required
					>
						<Input
							placeholder={t('pages.registration.form.inputs.lastName.placeholder')}
							{
								...register(
									"lastName", 
									{ required: true }
								)
							}
						/>
						{
							errors.lastName && 
							errors.lastName.type === 'required' && 
							<Text 
								color="red"
							>
								{t('pages.registration.form.inputs.lastName.errors.required')}
							</Text>
						}
					</Field>
				</GridItem>

				<GridItem colSpan={{ base: 2, lg: 1 }} >
					<Field
						label={<Text className="poppins-semibold" fontWeight="bold">{t('pages.registration.form.inputs.gender.label')}</Text>}
						required
					>
						<SelectRoot 
							collection={genders}
							onValueChange={option => {
								const value = option.value[0];
								setValue('gender', value)
							}}
							{...register("gender")}
						>
							<SelectTrigger cursor="pointer">
								<SelectValueText placeholder={t('pages.registration.form.inputs.gender.placeholder')} />
							</SelectTrigger>
							<SelectContent>
								{genders.items.map((gender) => (
									<SelectItem 
										cursor="pointer"
										item={gender} 
										key={gender.value}
										_hover={{ backgroundColor: '#DEDEDE' }}
									>
										{gender.label}
									</SelectItem>
								))}
							</SelectContent>
						</SelectRoot>
					</Field>
				</GridItem>

				<GridItem colSpan={{ base: 2, lg: 1 }} >
					<Field
						label={<Text className="poppins-semibold" fontWeight="bold">{t('pages.registration.form.inputs.language.label')}</Text>}
						required
					>
						<SelectRoot 
							collection={languages}
							onValueChange={option => {
								const value = option.value[0];
								setValue('language', value)
							}}
							{...register("language")}
						>
							<SelectTrigger cursor="pointer">
								<SelectValueText placeholder={t('pages.registration.form.inputs.language.placeholder')} />
							</SelectTrigger>
							<SelectContent>
								{languages.items.map((language) => (
									<SelectItem 
										cursor="pointer"
										item={language} 
										key={language.value}
										_hover={{ backgroundColor: '#DEDEDE' }}
									>
										{language.label}
									</SelectItem>
								))}
							</SelectContent>
						</SelectRoot>
					</Field>
				</GridItem>

				<GridItem colSpan={{ base: 2, lg: 1 }} >
					<Field
						label={<Text className="poppins-semibold" fontWeight="bold">{t('pages.registration.form.inputs.password.label')}</Text>}
						required
					>
						<PasswordInput
							placeholder={t('pages.registration.form.inputs.password.placeholder')}
							{
								...register(
									"password", 
									{ 
										required: true,
										pattern: {
											value: /^(?=.*?[A-Z])(?=.*?[a-z])(?=.*?[0-9]).{8,}$/,
											message: t('pages.security.passwordBox.modals.forms.alert'),
										}
									}
								)
							}
						/>
						{
							errors.password && 
							errors.password.type === 'required' && 
							<Text 
								color="red"
							>
								{t('pages.registration.form.inputs.password.errors.required')}
							</Text>
						}
						{errors.password?.type === 'pattern' && <Text color="red">{t('pages.security.passwordBox.modals.forms.alert')}</Text>}
					</Field>
				</GridItem>

				<GridItem colSpan={{ base: 2, lg: 1 }} >
					<Field
						label={<Text className="poppins-semibold" fontWeight="bold">{t('pages.registration.form.inputs.confirmPassword.label')}</Text>}
						required
					>
						<PasswordInput
							placeholder={t('pages.registration.form.inputs.confirmPassword.placeholder')}
							{
								...register(
									"confirmPassword", 
									{ 
										required: true,
										validate: value => {
											const { password } = getValues();
											return password === value;
										},
									}
								)
							}
						/>
						{
							errors.confirmPassword && 
							errors.confirmPassword.type === 'required' && 
							<Text 
								color="red"
							>
								{t('pages.registration.form.inputs.confirmPassword.errors.required')}
							</Text>
						}
						{errors.confirmPassword?.type === 'validate' && <Text color="red">{t('pages.security.passwordBox.modals.forms.messages.errors.match')}</Text>}
					</Field>
				</GridItem>

				{
					newsletterCollection &&
					<GridItem colSpan={2} marginBottom={4} marginTop={2} >
						<Fieldset.Root>
							<CheckboxGroup name="newsletters" onValueChange={newsletters.field.onChange}>
								<Fieldset.Legend fontSize="sm" mb="2" marginBottom={3}>
									<Text className="poppins-semibold" fontWeight="bold">{t('pages.registration.form.inputs.newsletters.label')}</Text>
								</Fieldset.Legend>
								<Fieldset.Content>
									<For each={newsletterCollection.models}>
										{(model) => (
											<Checkbox.Root 
												colorPalette={'black'} 
												key={model.urn} 
												value={model.reference}
												variant={'outline'}
												_hover={{ cursor: 'pointer' }}
											>
												<Checkbox.HiddenInput />
												<Checkbox.Control>
													<HiOutlineCheck />
												</Checkbox.Control>
												<Checkbox.Label>{model.label}</Checkbox.Label>
											</Checkbox.Root>
										)}
									</For>
								</Fieldset.Content>
							</CheckboxGroup>
						</Fieldset.Root>
					</GridItem>
				}


				{!!CAPTCHA_SITE_KEY && (
					<GridItem colSpan={2} width="100%"  >
						<div style={{transform:"scale(0.85)", transformOrigin:"0 0", marginTop: "5px"}}>
							<ReCAPTCHA 
								key={captchaKey}
								onChange={v => setCaptchaValue(v || '')}
								sitekey={CAPTCHA_SITE_KEY}
							/>
						</div>
					</GridItem>
				)}

				<GridItem colSpan={2} >
					<Button 
						variant="solid" 
						type="submit" 
						width="100%"
						marginTop="20px"
						loading={isSubmitting}
						disabled={!captchaValue}
					>
						{t('pages.registration.form.actions.register')}
					</Button>
				</GridItem>
			</Grid>

		</form>
	)
}

export default RegistrationForm